import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import Dropdown from '../../components/Common/Dropdown';
import { getProgress, getHomeworks, getVariants, getActivity } from '../../services/studentService';
import { computeCourseProgress } from '../../utils/progressStats';
import { isMistakeCleared } from '../../utils/mistakesStore';

/* ── Дизайн перенесён из Claude Design (Progress.dc.html), на токенах темы
   (адаптив светлая/тёмная) + стеклянные карточки. «Ритм занятий» — демо. ── */

const MO = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
// Периоды в «Всей истории» — готовые отрезки, считаются от сегодняшнего дня
const PERIODS = [
  { value: 'all', label: 'За всё время' },
  { value: '7',   label: 'За 7 дней' },
  { value: '30',  label: 'За 30 дней' },
  { value: '90',  label: 'За 3 месяца' },
];
const fmtD = (d) => { if (!d) return ''; const x = d instanceof Date ? d : new Date(d); return isNaN(x) ? '' : `${x.getDate()} ${MO[x.getMonth()]}`; };
const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };
const whenLabel = (ds) => {
  if (!ds) return '';
  const d = new Date(ds); if (isNaN(d)) return '';
  const t = new Date(); t.setHours(0,0,0,0);
  const dd = new Date(d); dd.setHours(0,0,0,0);
  const diff = Math.round((t - dd) / 86400000);
  if (diff === 0) return 'сегодня';
  if (diff === 1) return 'вчера';
  return fmtD(d);
};

/* Числовой шрифт как в макете */
const NUM = { fontFamily: "'Space Grotesk','Montserrat',sans-serif", fontWeight: 600 };
// Крупный процент в «Всего пройдено» — тем же шрифтом, что итоговый процент
// в шапке завершения ДЗ (.rb-pct).
const BIG = { fontFamily: "'Bebas Neue','Oswald',sans-serif", fontWeight: 400, letterSpacing: '1px' };

/* Иконки разделов */
const svgWeb = (s) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>);
const svgHw = (s) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const svgVariant = (s) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>);
const kindSvg = (k, s) => (k === 'hw' ? svgHw(s) : k === 'variant' ? svgVariant(s) : svgWeb(s));

const KIND_LABEL = { web: 'вебинар', hw: 'дз', variant: 'вариант' };
// Метка действия — те же чипы, что метки «Просмотрен»/«Пройден» в «Вебинарах»:
// зелёный на выполненное, янтарный на промежуточное, красный на ошибку.
const TONE_CHIP = { ok: 'green', bad: 'red', muted: 'amber' };
// Иконка в ленте активности одинаковая для всех типов — различает их сам значок,
// а не цвет плашки (раньше ДЗ был градиентом, вариант — серым).
const ACT_ICON = { iconBg: 'var(--primary-light)', iconFg: 'var(--primary-dark)', iconShadow: 'none' };
const ICON_STYLE = { hw: ACT_ICON, web: ACT_ICON, variant: ACT_ICON };

/* ── ДЕМО: тепловая карта «Ритм занятий» — цвета клеток фиксированы (одинаковы в обеих темах) ── */
const LEVEL_BG = ['#f1f0f6', 'rgba(124,77,255,0.24)', 'rgba(124,77,255,0.5)', '#7c4dff'];
const HEAT = [
  [0,1,0,2,1,0,0],
  [1,2,0,0,3,1,0],
  [0,1,1,2,0,0,1],
  [2,3,1,0,2,0,0],
  [1,2,3,1,0,0,0],
];
const DAY_LABELS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

const CAP = { fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' };

export default function StudentProgressPage() {
  const [progress, setProgress] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [variants, setVariants] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const { selectedCourseId, course, courseName } = useOutletContext() || {};
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getProgress(selectedCourseId).then(r => setProgress(r.data)).catch(() => {}),
      getHomeworks(selectedCourseId).then(r => setHomeworks(r.data || [])).catch(() => {}),
      getVariants(selectedCourseId).then(r => setVariants(r.data || [])).catch(() => {}),
      getActivity(selectedCourseId).then(r => setActivity(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [selectedCourseId]);

  // Период в «Всей истории»: фильтруем ленту активности по дате события
  const [period, setPeriod] = useState('all');
  const historyList = useMemo(() => {
    if (period === 'all') return activity;
    const from = Date.now() - Number(period) * 86400000;
    return activity.filter(a => a.date && new Date(a.date).getTime() >= from);
  }, [activity, period]);

  if (loading) return <div className="s-page"><Loader /></div>;

  // ── Единый расчёт (тот же, что на Главной в «Общий прогресс») ──
  const stats = computeCourseProgress(progress, homeworks, variants);
  const pct = stats.pct;

  // ── Неверные номера ЗАВЕРШЁННЫХ ДЗ → тренажёр «Работа над ошибками» (минус уже добитые за сеанс) ──
  const mistakeCount = homeworks.filter(h => h.lessonCompleted && h.answer?.status === 'incorrect' && !isMistakeCleared(h.id)).length;

  const leftWeb = Math.max(0, stats.web.total - stats.web.done);
  const leftHw = Math.max(0, stats.hw.total - stats.hw.done);

  const totals = [
    { kind: 'web', label: 'Вебинары', done: stats.web.done, total: stats.web.total },
    { kind: 'hw', label: 'Домашние задания', done: stats.hw.done, total: stats.hw.total },
    { kind: 'variant', label: 'Пробные варианты', done: stats.variant.done, total: stats.variant.total },
  ];

  const nothing = stats.web.total === 0 && stats.hw.total === 0 && stats.variant.total === 0;
  const headline = nothing ? 'Курс ещё не начат'
    : pct === 100 ? 'Курс пройден полностью'
    : pct >= 50 ? <>Курс пройден больше<br />чем наполовину</>
    : <>Вы в начале<br />пути</>;

  // ── Навигация из активности: вебинар → урок, ДЗ/вариант → страница ответов ──
  const goActivity = (a) => {
    if (!a.lessonId) return;
    const path = a.navKind === 'web' ? `/lesson/${a.lessonId}` : `/homework/${a.lessonId}`;
    navigate(path, { state: { from: '/progress' } });
  };

  const recent = activity.slice(0, 6);

  const bigSize = 92;   // Bebas узкая — при 78px смотрелась мельче, чем была

  // Строка активности (используется и в карточке, и в модалке «Вся история»)
  const ActRow = ({ a, i }) => {
    const ic = ICON_STYLE[a.kind] || ACT_ICON;
    return (
      <div key={i} className="pg2-act" onClick={() => goActivity(a)} style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: '13px 16px', cursor: 'pointer' }}>
        <span className="pg-actic" style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 13, background: ic.iconBg, color: ic.iconFg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: ic.iconShadow }}>{kindSvg(a.kind, 19)}</span>
        <div style={{ flex: '1 1 300px', minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</span>
            <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-3)', background: 'var(--bg)', padding: '2px 7px', borderRadius: 20, flexShrink: 0 }}>{KIND_LABEL[a.kind]}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.module}</div>
        </div>
        {/* 132 — под самую длинную метку «начал выполнять» */}
        <div style={{ flex: '0 0 auto', width: 132, display: 'flex', justifyContent: 'flex-end' }}>
          <span className={`pg-chip ${TONE_CHIP[a.tone] || 'amber'}`}>{a.action}</span>
        </div>
        <span style={{ flex: '0 0 auto', width: 76, textAlign: 'right', fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{whenLabel(a.date)}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)', flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
      </div>
    );
  };

  return (
    <div className="s-page">
      <style>{`
        .pg2-card { background: var(--lg-sheen), var(--card); border: 1px solid var(--lg-edge); border-radius: 18px;
          box-shadow: inset 0 1px 0 var(--lg-hi), inset 0 0 0 1px var(--lg-ring), var(--lg-shadow); }
        .pg2-tile { background: var(--lg-sheen), var(--card); border: 1px solid var(--lg-edge);
          box-shadow: inset 0 1px 0 var(--lg-hi), inset 0 0 0 1px var(--lg-ring), var(--lg-shadow); }
        /* Плитки делят всю свободную высоту карточки — снизу не остаётся пустоты */
        .pg2-totals { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .pg2-totals > .pg2-tile { flex: 1; min-height: 52px; }
        /* Линия прогресса плитки: ДЗ и варианты — зелёная, вебинары — контрастная
           (белая в тёмной теме, чёрная в светлой) */
        .pg2-tbar { height: 6px; border-radius: 10px; background: var(--border); overflow: hidden; }
        .pg2-tbar-in { height: 6px; border-radius: 10px; transition: width .3s; }
        .pg2-tbar-in.done { background: linear-gradient(90deg,#4ade80,#16a34a); }
        .pg2-tbar-in.web { background: var(--text-1); }
        /* Иконка плитки — в цвет своей линии, а не фиолетовая */
        /* Плитки тянутся по высоте карточки — иконка растёт вместе с ними:
           доля от высоты плитки, квадрат, значок внутри тоже в долях. */
        .pg-secic { height: 72%; min-height: 30px; max-height: 56px; aspect-ratio: 1 / 1; flex: 0 0 auto;
          border-radius: 12px; display: inline-flex; align-items: center; justify-content: center;
          background: var(--card); border: 1px solid var(--border); box-shadow: 0 1px 2px rgba(40,20,80,0.05); }
        .pg-secic svg { width: 50%; height: 50%; }
        .pg-secic.done { color: #16a34a; border-color: rgba(34,197,94,0.35); background: rgba(34,197,94,0.1); }
        .pg-secic.web { color: var(--text-1); }
        body.dark .pg-secic.done { color: #4ade80; }
        .pg2-grid { display:grid; grid-template-columns:repeat(12,1fr); gap:18px; align-items:stretch; }
        .pg2-c7 { grid-column:span 7; } .pg2-c5 { grid-column:span 5; } .pg2-full { grid-column:1 / -1; }
        @media (max-width:900px){ .pg2-c7,.pg2-c5 { grid-column:1 / -1; } .pg2-err { flex-wrap:wrap; } }
        /* Мобилка: «Работа над ошибками» и строки активности раскладываем в
           столбик. Значения flex заданы инлайном, поэтому перебиваем важностью. */
        @media (max-width:620px){
          .pg2-err { gap:14px !important; padding:16px !important; }
          .pg2-err > * { flex:1 1 100% !important; min-width:0 !important; }
          .pg2-err-hint { order:3; }
          .pg2-errbtn { justify-content:center; }
          /* строка активности: иконка+название сверху, статус и дата — снизу */
          .pg2-act { flex-wrap:wrap; gap:10px 12px !important; padding:14px !important; }
          .pg2-act > .pg-actic { flex:0 0 auto !important; }
          .pg2-act > div:first-of-type { flex:1 1 60% !important; min-width:0 !important; }
          .pg2-act > div:nth-of-type(2) { order:3; flex:0 0 auto !important; width:auto !important; }
          .pg2-act > span:last-of-type { order:4; margin-left:auto; width:auto !important; }
          .pg2-act > svg { display:none; }
        }
        .pg2-act { transition:transform .14s, box-shadow .14s, border-color .14s; }
        /* В тёмной теме фиолетовый на фиолетовом не читается — статус белым */
        .pg2-act:hover { border-color:var(--primary); box-shadow:0 12px 26px -18px rgba(124,77,255,0.5); transform:translateY(-1px); }
        .pg2-errbtn { transition:filter .15s; } .pg2-errbtn:hover { filter:brightness(1.06); }
        .pg2-errbtn:disabled { opacity:.5; cursor:not-allowed; filter:none; }
        /* Выбор периода в модалке «Вся история» */
        .pg2-period { flex:0 0 auto; width:160px; margin-left:auto; margin-right:10px; }
        .pg2-link:hover { color:var(--primary-dark) !important; }
        .pg2-demo { font-size:9.5px; font-weight:800; letter-spacing:0.5px; text-transform:uppercase; color:var(--text-3);
          background:var(--bg); border:1px solid var(--border); padding:2px 8px; border-radius:20px; }
        .pg2-modal-ov { position:fixed; inset:0; z-index:900; background:rgba(20,12,40,0.5); display:flex; align-items:center; justify-content:center; padding:20px; }
        .pg2-modal { width:min(780px,100%); max-height:84vh; display:flex; flex-direction:column; overflow:hidden;
          background:var(--lg-modal, var(--card)); border:1px solid var(--lg-edge); border-radius:20px; box-shadow:0 40px 90px -30px rgba(20,10,50,0.6);
          animation:pg2pop .22s cubic-bezier(.2,.9,.3,1); }
        @keyframes pg2pop { from { opacity:0; transform:translateY(10px) scale(.98); } to { opacity:1; transform:none; } }
        /* .pg-big и .pg-secic теперь красятся токенами (текст / зелёный), белым
           перебивать не нужно — осталась только иконка ленты активности. */
        body.dark .pg-actic { color:#fff !important; }
        /* «Вся история» — на тёмном фиолетовый на фиолетовом читается плохо */
        body.dark .pg2-link { color:#fff !important; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: "'Saar SP Demo','Montserrat',sans-serif", fontSize: 42, fontWeight: 400, letterSpacing: '0.8px', lineHeight: 1.08, textTransform: 'uppercase', color: 'var(--text-1)' }}>Прогресс</h1>
          <p style={{ fontSize: 14, color: 'var(--text-2)', marginTop: 6 }}>{courseName || 'Курс'} · домашние задания и вебинары</p>
        </div>
      </div>

      <div className="pg2-grid">

        {/* ── Всего пройдено ── */}
        <div className="pg2-c7 pg2-card" style={{ display: 'flex', flexDirection: 'column', padding: '22px 24px', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
            <div style={CAP}>Всего пройдено</div>
            {course?.startDate && <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>курс идёт с {fmtD(course.startDate)}</div>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexShrink: 0 }}>
              <span className="pg-big" style={{ ...BIG, fontSize: bigSize, lineHeight: 0.82, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{pct}</span>
              <span className="pg-big" style={{ ...BIG, fontSize: 34, color: 'var(--text-1)', paddingTop: 6 }}>%</span>
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingLeft: 24, borderLeft: '1px solid var(--border)' }}>
              <div style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 700, lineHeight: 1.35 }}>{headline}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 7, lineHeight: 1.5 }}>
                Осталось {leftWeb} {plural(leftWeb, ['вебинар','вебинара','вебинаров'])}<br />и {leftHw} {plural(leftHw, ['домашнее задание','домашних задания','домашних заданий'])}
              </div>
            </div>
          </div>

          <div className="pg2-totals">
            {totals.map((s, i) => {
              const w = s.total > 0 ? Math.round((s.done / s.total) * 100) : 0;
              return (
                <div key={i} className="pg2-tile" style={{ display: 'flex', alignItems: 'center', gap: 14, borderRadius: 13, padding: '11px 14px' }}>
                  <span className={`pg-secic ${s.kind === 'web' ? 'web' : 'done'}`}>{kindSvg(s.kind, 15)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 7 }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}>{s.done}<span style={{ color: 'var(--text-3)', fontWeight: 600 }}> / {s.total}</span></span>
                    </div>
                    <div className="pg2-tbar">
                      <div className={`pg2-tbar-in ${s.kind === 'web' ? 'web' : 'done'}`} style={{ width: `${w}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Ритм занятий (демо) ── */}
        <div className="pg2-c5 pg2-card" style={{ display: 'flex', flexDirection: 'column', padding: '22px 24px', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={CAP}>Ритм занятий</div>
              <span className="pg2-demo">Демо</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>5 недель</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 7 }}>
            {DAY_LABELS.map((l, i) => <span key={i} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700, color: 'var(--text-3)', letterSpacing: '0.3px' }}>{l}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
            {HEAT.flat().map((lvl, i) => (
              <div key={i} title={lvl ? `${lvl} ${lvl === 1 ? 'действие' : 'действия'}` : 'без активности'} style={{ width: '100%', aspectRatio: '1', borderRadius: 6, background: LEVEL_BG[lvl] }} />
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 'auto', paddingTop: 16 }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600 }}>18 активных дней из 35</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {LEVEL_BG.map((c, i) => <span key={i} style={{ width: 10, height: 10, borderRadius: 3, background: c }} />)}
            </div>
          </div>
        </div>

        {/* ── Работа над ошибками ── */}
        <div className="pg2-full pg2-err pg2-card" style={{ display: 'flex', alignItems: 'center', gap: 22, padding: '18px 22px' }}>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 15 }}>
            <div style={{ minWidth: 56, height: 56, flexShrink: 0, padding: '0 13px', borderRadius: 16, background: mistakeCount > 0 ? 'linear-gradient(150deg,#f87171,#dc2626)' : 'linear-gradient(150deg,#4ade80,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: mistakeCount > 0 ? '0 8px 20px -10px rgba(220,38,38,0.75)' : '0 8px 20px -10px rgba(22,163,74,0.7)' }}>
              <span style={{ ...NUM, fontSize: mistakeCount > 99 ? 25 : 34, color: '#fff', lineHeight: 1, letterSpacing: '-0.5px', fontVariantNumeric: 'tabular-nums' }}>{mistakeCount}</span>
            </div>
            <div>
              <div style={CAP}>Работа над ошибками</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600, marginTop: 5, whiteSpace: 'nowrap' }}>{mistakeCount > 0 ? `${plural(mistakeCount, ['номер','номера','номеров'])} с ошибками` : 'ошибок нет'}</div>
            </div>
          </div>

          <div className="pg2-err-hint pg2-tile" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 16, borderRadius: 13, padding: '13px 16px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ color: mistakeCount > 0 ? '#dc2626' : '#16a34a', flexShrink: 0 }}><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{mistakeCount > 0 ? 'Ошибки — самая короткая дорога к баллам' : 'Все ошибки разобраны'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{mistakeCount > 0 ? 'Прорешай неверные номера из завершённых ДЗ — они уходят из списка, как только решишь их верно' : 'В завершённых ДЗ нет номеров с ошибками'}</div>
            </div>
          </div>

          <button className="pg2-errbtn" onClick={() => navigate('/mistakes', { state: { from: '/progress' } })} disabled={mistakeCount === 0} style={{ flex: '0 0 auto', padding: '11px 19px', border: 'none', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12.5, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', boxShadow: '0 8px 20px -10px rgba(124,77,255,0.8)', display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>Разобрать ошибки
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>

        {/* ── Недавняя активность ── */}
        <div className="pg2-full pg2-card" style={{ padding: '22px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
            <div style={CAP}>Недавняя активность</div>
            {activity.length > 0 && (
              <button className="pg2-link" onClick={() => setShowHistory(true)} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Вся история</button>
            )}
          </div>

          {recent.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '10px 0' }}>Активности пока нет</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {recent.map((a, i) => <ActRow key={i} a={a} i={i} />)}
            </div>
          )}
        </div>
      </div>

      {/* ── Модалка «Вся история» ── */}
      {showHistory && (
        <div className="pg2-modal-ov" onClick={() => setShowHistory(false)}>
          <div className="pg2-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)', letterSpacing: '-0.3px' }}>Вся история</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{historyList.length} {plural(historyList.length, ['событие','события','событий'])} · {courseName || 'Курс'}</div>
              </div>
              <div className="pg2-period">
                <Dropdown value={period} onChange={setPeriod} options={PERIODS} size="sm" />
              </div>
              <button onClick={() => setShowHistory(false)} style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 9 }}>
              {historyList.length === 0
                ? <div style={{ padding: '30px 10px', textAlign: 'center', fontSize: 13.5, color: 'var(--text-3)' }}>За выбранный период событий нет</div>
                : historyList.map((a, i) => <ActRow key={i} a={a} i={i} />)}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
